<script setup lang="ts">
import { ref, watch } from 'vue';

import { useAttachmentUpload } from '@satellite/composables/useAttachmentUpload';
import {
  ATTACHMENT_ACCEPTED_MIME_TYPES,
  ATTACHMENT_MAX_SIZE_MB,
  getAttachmentRejection,
  type AttachmentRejectionReason
} from '@satellite/helpers/attachmentUtils';

import type { DatabaseEntities, Maybe, Nullable } from '@nova/core';

defineOptions({ name: 'AttachmentUploadButton' });

const {
  relatedEntity,
  relatedId,
  label,
  testId = 'attachment'
} = defineProps<{
  /** Which entity the documents hang off, e.g. `DatabaseEntities.YardTask`. */
  relatedEntity: DatabaseEntities;
  /** The entity's id. Nothing is uploaded until there is one. */
  relatedId: Maybe<string>;
  /** Accessible name for the control, e.g. "Upload a file". */
  label: string;
  /** Prefix for every `data-testid` this control renders, e.g. `<testId>-upload-button`. */
  testId?: string;
}>();

/**
 * Read-only for the caller: the upload is this control's, but the tiles beside it may want to
 * settle while one is in flight, and only the caller can reach them.
 */
const isUploading = defineModel<boolean>('isUploading', { default: false });

const emit = defineEmits<{
  uploaded: [];
  /** A file refused before any request was made; the wording is the caller's. */
  rejected: [reason: AttachmentRejectionReason, maxSizeMb: number];
  error: [error: unknown];
}>();

const fileInput = ref<Nullable<HTMLInputElement>>(null);

const { uploadAttachment, isUploading: isRequestPending } = useAttachmentUpload({
  relatedEntity: () => relatedEntity,
  relatedId: () => relatedId,
  onSuccess: () => emit('uploaded'),
  onError: error => emit('error', error)
});

watch(isRequestPending, pending => {
  isUploading.value = pending;
});

/**
 * No `capture` attribute: leaving the source to the OS is the point. A hint here is what made
 * mobile show its own file/photo picker on top of the app's, forcing a pick twice over.
 */
async function handleFileChange(event: Event): Promise<void> {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];

  // Reset first so picking the same file twice in a row still fires `change`.
  input.value = '';

  if (!file) {
    return;
  }

  const rejection = getAttachmentRejection(file);
  if (rejection) {
    emit('rejected', rejection, ATTACHMENT_MAX_SIZE_MB);
    return;
  }

  await uploadAttachment(file);
}
</script>

<template>
  <!--
    Out of the accessibility tree on purpose: the button beside it carries the label and the
    action, so an unnamed file input would only be a second, mute control to land on.
  -->
  <input
    ref="fileInput"
    type="file"
    class="d-sr-only"
    aria-hidden="true"
    tabindex="-1"
    :accept="ATTACHMENT_ACCEPTED_MIME_TYPES"
    :data-testid="`${testId}-file-input`"
    @change="handleFileChange" />

  <v-btn
    variant="text"
    class="attachment-upload-button"
    :disabled="isRequestPending || !relatedId"
    :loading="isRequestPending"
    :aria-label="label"
    :data-testid="`${testId}-upload-button`"
    @click="fileInput?.click()">
    <v-icon icon="mdi-tray-arrow-up" size="24" />
  </v-btn>
</template>

<style lang="scss" scoped>
/* Sized to match the attachment tiles it sits alongside. */
.attachment-upload-button {
  width: 72px;
  height: 72px;
  min-width: 0;
  padding: $spacing-1;
  border: 1px dashed $color-line-divider;
  border-radius: $default-border-radius;
  background-color: transparent;
  color: inherit;
}
</style>
